<template>
  <div class="chat-container">
    <h1 class="chat-title">AI猫娘-小喵</h1>
    <div ref="conversationContainer" class="conversation-container">
      <div
        v-for="(message, index) in conversation"
        :key="index"
        :class="message.role === 'user' ? 'user-message' : 'ai-message'"
      >
        <strong>{{ message.role === "user" ? "主人:" : "小喵:" }}</strong>
        <span :class="{ 'thinking-text': message.thinking }">{{
          formatContent(message.content)
        }}</span>
        <!-- 仅在最新的 AI 消息框右边显示旋转圆圈喵 -->
        <span
          v-if="message.thinking && index === conversation.length - 1"
          class="loading-circle"
        ></span>
      </div>
    </div>
    <div class="input-container">
      <textarea
        ref="input"
        v-model="input"
        placeholder="Type your message here..."
        @keydown.enter="handleSubmit"
        @compositionstart="isComposing = true"
        @compositionend="isComposing = false"
        class="chat-input"
        :disabled="isThinking"
        rows="1"
        @input="autoResize"
        spellcheck="false"
        autocomplete="off"
        autocorrect="off"
        inputmode="text"
      ></textarea>
      <button @click="handleSubmit" class="send-button" :disabled="isThinking">
        Send
      </button>
    </div>
  </div>
</template>

<script>
const API_BASE_URL = process.env.VUE_APP_API_BASE;
export default {
  data() {
    return {
      input: "",
      conversation: [], // 对话历史
      isThinking: false, // 是否正在思考喵
      isComposing: false, // 跟踪输入法状态
    };
  },
  mounted() {
    this.$refs.input.addEventListener("compositionstart", () => {
      this.isComposing = true;
    });
    this.$refs.input.addEventListener("compositionend", () => {
      this.isComposing = false;
    });
  },
  methods: {
    async handleSubmit(event) {
      event.preventDefault();
      if (event.shiftKey || !this.input.trim() || this.isThinking) {
        // 如果按下 Shift+Enter 允许换行，否则直接返回喵
        if (!event.shiftKey) return;
        this.input += "\n";
        return;
      }
      const { selectionStart, selectionEnd } = event.target;
      const isComposing = event.isComposing;
      // 添加用户消息到对话历史
      this.conversation.push({ role: "user", content: this.input });

      // 清空输入框
      this.input = "";

      // 开始思考喵
      this.isThinking = true;

      // 添加“正在思考中”的消息到对话历史
      this.conversation.push({
        role: "assistant",
        content: "Thinking...",
        thinking: true,
      });

      // 强制更新 DOM 并滚动到最下方喵
      this.scrollToBottom();

      try {
        // 调用后端 API
        const response = await fetch(`${API_BASE_URL}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            input: this.conversation[this.conversation.length - 2].content,
            conversation: this.conversation,
          }),
        }).then((res) => res.json());

        if (response.success) {
          // 更新“正在思考中”的消息为 AI 的最终响应喵
          const lastMessage = this.conversation[this.conversation.length - 1];
          if (lastMessage.role === "assistant") {
            lastMessage.content = response.response;
            lastMessage.thinking = false; // 标记为非思考状态喵
          }
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error("Error fetching AI response:", error);
        // 更新“正在思考中”的消息为错误提示喵
        const lastMessage = this.conversation[this.conversation.length - 1];
        if (lastMessage.role === "assistant") {
          lastMessage.content = "Oops! Something went wrong. Please try again.";
          lastMessage.thinking = false; // 标记为非思考状态喵
        }
      } finally {
        this.isThinking = false;
        this.scrollToBottom();

        // 使用nextTick确保DOM更新完成喵
        this.$nextTick(() => {
          const input = this.$refs.input;
          if (!input) return;

          // 恢复输入法状态
          input.focus();

          // 特殊处理中文输入法问题
          if (isComposing || navigator.userAgent.indexOf("Chrome") > -1) {
            // 强制刷新IME状态
            const tmp = input.value;
            input.value = "";
            setTimeout(() => {
              input.value = tmp;
              input.setSelectionRange(selectionStart, selectionEnd);
            }, 10);
          }
        });
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.conversationContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    formatContent(content) {
      // 将 `\n` 解析为回车符，实现换行效果喵
      return content.replace(/\n/g, "\n");
    },
    autoResize(event) {
      const textarea = event.target;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    },
  },
};
</script>

<style scoped>
/* 样式优化部分喵 */
.chat-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 550px;
  margin: 20px auto;
  padding: 20px;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  display: flex;
  flex-direction: column;
}

.chat-title {
  font-size: 21px; /* 缩小字体大小喵 */
  color: #333;
  text-align: center;
  margin-bottom: 18px; /* 缩小间距喵 */
  font-weight: 600;
}

.conversation-container {
  flex: 1;
  margin-bottom: 18px; /* 缩小间距喵 */
  padding: 18px; /* 缩小内边距喵 */
  background-color: #fafafa;
  border-radius: 10px; /* 缩小圆角喵 */
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.05);
  overflow-y: auto;
}

.user-message,
.ai-message {
  margin-bottom: 12px; /* 缩小间距喵 */
  padding: 9px 14px; /* 缩小内边距喵 */
  border-radius: 9px; /* 缩小圆角喵 */
  font-size: 12px; /* 缩小字体大小喵 */
  line-height: 1.5;
  max-width: 80%; /* 最大宽度喵 */
  width: fit-content;
  position: relative;
  animation: fade-in 0.3s ease;
  word-break: break-word;
  text-align: left;
  white-space: pre-line;
}

.user-message {
  background-color: #007bff;
  color: #fff;
  margin-left: auto;
}

.ai-message {
  background-color: #f0f0f0;
  color: #333;
  margin-right: auto;
}

.thinking-text {
  color: #999;
  font-style: italic;
}

.loading-circle {
  display: inline-block;
  margin-left: 6px; /* 缩小间距喵 */
  width: 12px; /* 缩小尺寸喵 */
  height: 12px; /* 缩小尺寸喵 */
  border: 2px solid #ccc;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.input-container {
  display: flex;
  gap: 8px; /* 缩小间距喵 */
  padding: 10px; /* 缩小内边距喵 */
  background-color: #fafafa;
  border-radius: 8px; /* 缩小圆角喵 */
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.05);
}

.chat-input {
  flex: 1;
  padding: 9px 14px; /* 缩小内边距喵 */
  border: 1px solid #ddd;
  border-radius: 6px; /* 缩小圆角喵 */
  font-size: 12px; /* 缩小字体大小喵 */
  outline: none;
  transition: border-color 0.3s ease;
  background-color: #fff;
  color: #333;
  resize: none;
  overflow: hidden;
  min-height: 16px; /* 缩小最小高度喵 */
}

.chat-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 4px rgba(0, 123, 255, 0.3);
}

.send-button {
  padding: 9px 18px; /* 缩小内边距喵 */
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 6px; /* 缩小圆角喵 */
  font-size: 12px; /* 缩小字体大小喵 */
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.send-button:hover {
  background-color: #0056b3;
}

.send-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
